import type { LeadKind, PageLayout, PieceRole, PreviewDoc, PreviewPiece } from "../types";

/** What each piece of a page of several is to the page, and which version of
 *  the page this reader is shown.
 *
 *  The server decides both (getPreview's pieceRoles and pageLayout, in
 *  functions/src/lib/pieceRoles.ts) and sends them on each piece and as
 *  `layout`. This is the same rule on the client, for a server that does not
 *  send them yet (a page read before the deploy, or a build of the page ahead
 *  of the functions): the two must say the same thing, so change them
 *  together. */

/** The index of the piece the page is about: the Pair whenever there is one
 *  (the first of them), else the first piece that is the page's own product
 *  (`pageKind`, what the lead is quoted as), else simply the first. -1 for no
 *  pieces.
 *
 *  The Pair first because a couple who asked for the two of them together
 *  asked for the Pair, whatever order the drawings were made in; and the page's
 *  own product next because that is what the lead was asked as, so a dog's
 *  keychain listed before the person's statue does not become the page of a
 *  request for the statue. */
function primaryAt(kinds: LeadKind[], pageKind: LeadKind): number {
  const duo = kinds.indexOf("duo");
  if (duo >= 0) return duo;
  const own = kinds.indexOf(pageKind);
  if (own >= 0) return own;
  return kinds.length ? 0 : -1;
}

/** Two products of the same people: a single beside their Pair, a head
 *  keychain beside a pet one. Either way round. */
function sameSubjects(a: LeadKind, b: LeadKind): boolean {
  return (
    (a === "duo" && b === "minime") ||
    (a === "minime" && b === "duo") ||
    (a === "head_keychain" && b === "pet_keychain") ||
    (a === "pet_keychain" && b === "head_keychain")
  );
}

/** The role of each piece, in the order they are given: one "primary", every
 *  other an "alternative" (the same people as another product, offered as a
 *  swap) or an "extra" (another subject, which can join the same parcel).
 *
 *  The server's own word wins when it gave one to every piece. Empty for fewer
 *  than two pieces: a page of one has nothing to be a role of. */
export function rolesOf(pieces: PreviewPiece[], pageKind: LeadKind): PieceRole[] {
  if (pieces.length < 2) return [];
  if (pieces.every((piece) => !!piece.role)) return pieces.map((piece) => piece.role!);
  const kinds = pieces.map((piece) => piece.kind ?? pageKind);
  const main = primaryAt(kinds, pageKind);
  return kinds.map((kind, at): PieceRole =>
    at === main ? "primary" : sameSubjects(kinds[main]!, kind) ? "alternative" : "extra"
  );
}

/** The version of the page this reader is shown: the server's, which is
 *  decided by the token so that a reader always sees the same one. The
 *  owner's own look (?admin=1) may override either half from the address,
 *  ?layout=row|cards and ?priceline=1|0, to see the version a customer would
 *  not be shown; a customer typing them changes nothing. Without a layout from
 *  the server, the row without the price line, which is the page's default
 *  until the test says otherwise. */
export function layoutOf(preview: PreviewDoc, params: URLSearchParams, admin: boolean): PageLayout {
  const base: PageLayout = preview.layout ?? { pieces: "row", priceLine: false };
  if (!admin) return base;
  const pieces = params.get("layout");
  const line = params.get("priceline");
  return {
    pieces: pieces === "row" || pieces === "cards" ? pieces : base.pieces,
    priceLine: line === "1" ? true : line === "0" ? false : base.priceLine,
  };
}
